// @flow

import { Download } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
	DataGridPremium, gridFilterModelSelector, gridSortModelSelector, gridVisibleColumnDefinitionsSelector,
	useGridApiRef
} from "@mui/x-data-grid-premium";
import axios from "axios";
import dayjs from "dayjs";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthenticatedLink } from "../../components/custom/general/authenticatedLink/authenticatedLink";
import PageContentWrapper from "../../components/mui/app/pageContentWrapper/pageContentWrapper";
import PageTitle from "../../components/mui/app/pageTitle/pageTitle";
import SkeletonLoaderWrapper from "../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { CustomPagination } from "../../components/mui/grids/dataGridCustom/customPagination";
import CustomToolbar from "../../components/mui/grids/dataGridCustom/customToolbar/customToolbar";
import DataGridWrapper from "../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { API, checkListOptions, trimesterCheckListOptions } from "../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import { downloadFile } from "../../helpers/fileHandle";
import { muiGridLocales } from "../../helpers/mui/muiLocales";
import {
	access_check_lists_history,
	check_lists_column_site,
	check_lists_filter_site
} from "../../helpers/rights/rights";
import { useMuiGridServerSide } from "../../hooks/muiGrid/useMuiGridServerSide";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";
import { CHECKLISTS } from "../../locales/pages/namespaces";
import { withPermissionHOC } from "../../modules/permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../modules/permission/hooks/useHasPermission";
import TruncateDescription from "../dashboard/partials/truncateDescription";
import Filters from "./partials/checkListFilters";

type Props = {};

export const initialFilters = {
	site: null,
	month: null,
	isSubmitted: '',
}

const GridToolbar = ({ exportData, t, loading }) => {
	return (
		<CustomToolbar
			showDensity={false}
			showExport={false}
		>
			<Button
				onClick={exportData}
				loading={loading}
			>
				{t('Export')}
			</Button>
		</CustomToolbar>
	)
}

export const mapFilters = (filters) => {
	const {
		site,
		month,
		isSubmitted
	} = filters
	return {
		...(site && site.id && { ucAddrId: site.id }),
		...(month && month.isValid() && { ucReferenceDt: dayjs(month).format('YYYY-MM-DD') }),
		...(typeof isSubmitted === 'number' && { isSubmitted: isSubmitted })
	}
}

const CheckListsHistory = (props: Props) => {
	const { t } = useTranslation(CHECKLISTS)
	const apiRef = useGridApiRef();
	const { token, isLoggedIn, language, dateFormat, timeFormat } = useUserData();
	const { userLogout } = useUserAction()
	const [filters, setFilters] = useState(initialFilters)
	const [loadingExport, setLoadingExport] = useState(false)
	const [loadingFilterData, setLoadingFilterData] = useState(false)
	const [sites, setSites] = useState([])
	const viewSiteFilter = useHasPermission([check_lists_filter_site], { shouldView: true })
	const viewSiteColumn = useHasPermission([check_lists_column_site], { shouldView: true })
	
	useEffect(() => {
		if (viewSiteFilter) {
			setLoadingFilterData(true)
			axios({
				method: 'post',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				data: {
					query: '',
					Type: 'Site',
					CustId: 1314, // Μόνο η alpha bank έχει καλώς έχειν
				},
				url: `${API}/en/customer/address/search`
			}).then((response) => {
				setSites(_.orderBy(response.data.data, ['Description'], ['asc']))
				setLoadingFilterData(false)
			}).catch((error) => {
				setLoadingFilterData(false)
				enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
					variant: 'error',
					autoHideDuration: 3000,
				})
				logoutUnauthorizedUser(error, isLoggedIn, userLogout)
			})
		}
	}, []);
	
	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		dataManager,
		convertSortModel,
		convertFilterModel
	} = useMuiGridServerSide({
		url: 'custom/goodtohave/index',
		initialPageSize: 50,
		initialSortModel: [{ name: 'id', direction: 'descending' }],
		otherDataParams: {
			...mapFilters(filters)
		}
	})
	const exportData = useCallback(() => {
		const sortModel = gridSortModelSelector(apiRef)
		const where = gridFilterModelSelector(apiRef)
		const columns = gridVisibleColumnDefinitionsSelector(apiRef)
		const data = {
			sorted: convertSortModel(sortModel),
			where: convertFilterModel(where),
			columns: columns.filter(f => f.type !== 'custom' && f.type !== 'actions').map(i => ({
				id: i.field,
				title: i.headerName
			})),
			...mapFilters(filters)
		}
		setLoadingExport(true)
		axios({
			url: `${API}/en/custom/goodtohave/export-excel`,
			method: 'post',
			responseType: "blob",
			data: data,
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		}).then((response) => {
			const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			const href = window.URL.createObjectURL(blob)
			downloadFile(href, 'export-excel-' + dayjs().format('YYYY-MM-DD HH:mm'))
			setLoadingExport(false)
		}).catch((error) => {
			setLoadingExport(false)
			const errorMessage = error?.status === 404 ? 'No data found to export!' : 'Export failed'
			enqueueSnackbar(handleAxiosErrors(error, t(errorMessage)), {
				variant: 'error',
				autoHideDuration: null,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [apiRef, convertSortModel, convertFilterModel, filters, t, isLoggedIn, userLogout])
	
	const columns = useMemo(() => [
		{
			field: 'id',
			headerName: t('PDF export'),
			description: t('PDF export'),
			type: 'actions',
			width: 100,
			align: 'center',
			renderCell: (params) => {
				return params.row.isSubmitted === "1" && (
					<AuthenticatedLink
						filename={'export-pdf-' + dayjs().format('YYYY-MM-DD HH:mm') + '.pdf'}
						url={`${API}/en/custom/goodtohave/export-pdf/${params.row.id}`}
					>
						<Tooltip title={t('PDF export')}>
							<IconButton>
								<Download fontSize="small"/>
							</IconButton>
						</Tooltip>
					</AuthenticatedLink>
				)
			}
		},
		...(viewSiteColumn ? [{
			field: 'Site',
			headerName: t('Site'),
			description: t('Site'),
			minWidth: 400,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		}] : []),
		{
			field: 'ucReferenceDt',
			headerName: t('Month'),
			description: t('Month'),
			flex: 1,
			minWidth: 100,
			type: 'date',
			align: 'center',
			filterable: false,
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format('MMMM YYYY') : null)
			}
		},
		{
			field: 'isSubmitted',
			headerName: t('Submitted'),
			description: t('Submitted'),
			flex: 1,
			minWidth: 100,
			type: 'boolean',
			valueGetter: (value, row, column, apiRef) => value === '1'
		},
		{
			field: 'ucSubmitDt',
			headerName: t('Submit Date'),
			description: t('Submit Date'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
		{
			field: 'ucSignName',
			headerName: t('Certified By'),
			description: t('Certified By'),
			minWidth: 180,
			flex: 1,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		// {
		// 	field: 'ucDailyTasks',
		// 	headerName: t('Daily Tasks'),
		// 	description: t('Daily Tasks'),
		// 	minWidth: 220,
		// 	flex: 1,
		// 	type: 'singleSelect',
		// 	valueOptions: checkListOptions.map(i => ({ ...i, label: t(i.label) })),
		// 	renderCell: (params) => {
		// 		const value = checkListOptions.find(i => i.value === params.value)?.label
		// 		return <TruncateDescription value={t(value)}/>
		// 	},
		// },
		// {
		// 	field: 'ucDailyComment',
		// 	headerName: t('Comment (Daily Tasks)'),
		// 	description: t('Comment (Daily Tasks)'),
		// 	minWidth: 200,
		// 	flex: 1,
		// 	renderCell: (params) => {
		// 		return <TruncateDescription value={params.value}/>
		// 	},
		// },
		// {
		// 	field: 'ucWeeklyTasks',
		// 	headerName: t('Weekly Tasks'),
		// 	description: t('Weekly Tasks'),
		// 	minWidth: 200,
		// 	flex: 1,
		// 	type: 'singleSelect',
		// 	valueOptions: checkListOptions.map(i => ({ ...i, label: t(i.label) })),
		// 	renderCell: (params) => {
		// 		const value = checkListOptions.find(i => i.value === params.value)?.label
		// 		return <TruncateDescription value={t(value)}/>
		// 	},
		// },
		// {
		// 	field: 'ucWeeklyComment',
		// 	headerName: t('Comment (Weekly Tasks)'),
		// 	description: t('Comment (Weekly Tasks)'),
		// 	minWidth: 200,
		// 	flex: 1,
		// 	renderCell: (params) => {
		// 		return <TruncateDescription value={params.value}/>
		// 	},
		// },
		// {
		// 	field: 'ucMonthlyTasks',
		// 	headerName: t('Monthly Tasks'),
		// 	description: t('Monthly Tasks'),
		// 	minWidth: 220,
		// 	flex: 1,
		// 	type: 'singleSelect',
		// 	valueOptions: checkListOptions.map(i => ({ ...i, label: t(i.label) })),
		// 	renderCell: (params) => {
		// 		const value = checkListOptions.find(i => i.value === params.value)?.label
		// 		return <TruncateDescription value={t(value)}/>
		// 	},
		// },
		// {
		// 	field: 'ucMonthlyComment',
		// 	headerName: t('Comment (Monthly Tasks)'),
		// 	description: t('Comment (Monthly Tasks)'),
		// 	minWidth: 200,
		// 	flex: 1,
		// 	renderCell: (params) => {
		// 		return <TruncateDescription value={params.value}/>
		// 	},
		// },
		// {
		// 	field: 'ucTrimesterTasks',
		// 	headerName: t('Trimester Tasks'),
		// 	description: t('Trimester Tasks'),
		// 	minWidth: 220,
		// 	flex: 1,
		// 	type: 'singleSelect',
		// 	valueOptions: trimesterCheckListOptions.map(i => ({ ...i, label: t(i.label) })),
		// 	renderCell: (params) => {
		// 		const value = trimesterCheckListOptions.find(i => i.value === params.value)?.label
		// 		return <TruncateDescription value={t(value)}/>
		// 	},
		// },
		// {
		// 	field: 'ucTrimesterComment',
		// 	headerName: t('Comment (Trimester Tasks)'),
		// 	description: t('Comment (Trimester Tasks)'),
		// 	minWidth: 200,
		// 	flex: 1,
		// 	renderCell: (params) => {
		// 		return <TruncateDescription value={params.value}/>
		// 	},
		// }
	], [t, dateFormat])
	
	return (
		<Box>
			<PageContentWrapper>
				<PageTitle
					title={t('Check Lists History')}/>
				<SkeletonLoaderWrapper loading={loadingFilterData}>
					<Filters
						filters={filters}
						setFilters={setFilters}
						dataManager={dataManager}
						loading={isLoading}
						sites={sites}
					/>
				</SkeletonLoaderWrapper>
				<DataGridWrapper sx={{ height: 700 }}>
					<DataGridPremium
						apiRef={apiRef}
						columns={columns}
						rows={data}
						pagination
						paginationModel={{ page, pageSize }}
						paginationMode="server"
						onPaginationModelChange={onPaginationModelChange}
						rowCount={Number(total)}
						pageSizeOptions={[5, 10, 15, 20, 50, 100]}
						sortingMode="server"
						onSortModelChange={(sortModel) => onSortModelChange(sortModel)}
						filterMode="server"
						onFilterModelChange={onFilterModelChange}
						loading={isLoading}
						localeText={muiGridLocales(language)}
						initialState={{
							density: 'compact',
						}}
						slots={{
							toolbar: GridToolbar,
							pagination: CustomPagination,
						}}
						slotProps={{
							toolbar: {
								exportData: exportData,
								t: t,
								loading: loadingExport
							}
						}}
					/>
				</DataGridWrapper>
			</PageContentWrapper>
		</Box>
	);
};

export default withPermissionHOC(CheckListsHistory, [access_check_lists_history])